import { Trans, useLingui } from '@lingui/react/macro'
import { Checkbox } from 'designSystem/components/Checkbox'
import { intervalPatch } from 'features/timeSeries/experiment/intervalPatch'
import { KNOB_HELP } from 'features/timeSeries/experiment/knobCopy'
import {
  KnobCheckRow,
  KnobField,
  KnobHint,
  KnobInput,
  KnobRow,
  KnobSelect,
} from 'features/timeSeries/experiment/knobFields'
import { KnobHelp } from 'features/timeSeries/experiment/KnobHelp'
import type {
  ExperimentConfig,
  IntervalUnit,
  SamplingMode,
} from 'features/timeSeries/experiment/types'
import type { LabBucketsOnChart } from 'features/timeSeries/experiment/useLabBucketsOnChart'
import { FC, Fragment, ReactNode } from 'react'

const isOneOf = <T extends string>(
  options: readonly T[],
  value: string
): value is T => (options as readonly string[]).includes(value)

/**
 * Renders an annotation for one config field — used by the sentinel-design plan
 * review to put the agent's rationale (and any warning about that field) next
 * to the control that set it. Omitted in the Experiment Lab.
 */
export type KnobNoteRenderer = (field: string) => ReactNode

export interface SamplingKnobsProps {
  config: ExperimentConfig
  onPatch: (patch: Partial<ExperimentConfig>) => void
  noteFor?: KnobNoteRenderer
  /**
   * Mirrors this grid onto the chart's pens. Supplied where a chart is on
   * screen to mirror onto; omitted elsewhere, and the toggle is then absent.
   */
  chartBuckets?: LabBucketsOnChart
}

/**
 * How rows are put on a shared time grid: sampling mode, bin width, the
 * aggregation applied inside a bin, and the empty-bin policy.
 */
const INTERVAL_UNITS: readonly IntervalUnit[] = ['seconds', 'minutes', 'hours']

export const SamplingKnobs: FC<SamplingKnobsProps> = ({
  config,
  onPatch,
  noteFor,
  chartBuckets,
}) => {
  const { t } = useLingui()

  const note = (field: string) => <Fragment>{noteFor?.(field)}</Fragment>
  return (
    <>
      <KnobField>
        <KnobHelp label={t`Sampling mode`} help={KNOB_HELP.samplingMode} />
        <KnobSelect
          value={config.sampling_mode}
          onChange={(e) =>
            onPatch({ sampling_mode: e.target.value as SamplingMode })
          }>
          <option value='aggregate'>
            <Trans>Aggregate onto grid (prod default)</Trans>
          </option>
          <option value='raw'>
            <Trans>Raw historian points (all available)</Trans>
          </option>
        </KnobSelect>
        {note('sampling_mode')}
      </KnobField>
      {config.sampling_mode === 'aggregate' && (
        <>
          <KnobRow>
            <KnobField style={{ flex: 1 }}>
              <KnobHelp label={t`Interval`} help={KNOB_HELP.interval} />
              <KnobInput
                type='number'
                min={1}
                value={config.interval}
                onChange={(e) =>
                  onPatch(
                    intervalPatch(config, {
                      interval: Number(e.target.value) || 10,
                    })
                  )
                }
              />
              {note('interval')}
            </KnobField>
            <KnobField>
              <KnobHelp label={t`Unit`} help={KNOB_HELP.intervalUnit} />
              <KnobSelect
                value={config.interval_unit}
                onChange={(e) => {
                  if (isOneOf(INTERVAL_UNITS, e.target.value)) {
                    onPatch(intervalPatch(config, { unit: e.target.value }))
                  }
                }}>
                <option value='seconds'>seconds</option>
                <option value='minutes'>minutes</option>
                <option value='hours'>hours</option>
              </KnobSelect>
              {note('interval_unit')}
            </KnobField>
          </KnobRow>
          <KnobField>
            <KnobHelp label={t`Aggregation`} help={KNOB_HELP.aggregation} />
            <KnobSelect
              value={config.aggregation_op}
              onChange={(e) =>
                onPatch({
                  aggregation_op: e.target
                    .value as ExperimentConfig['aggregation_op'],
                })
              }>
              <option value='mean'>mean</option>
              <option value='median'>median</option>
              <option value='min'>min</option>
              <option value='max'>max</option>
              <option value='last_value'>last_value</option>
            </KnobSelect>
            {note('aggregation_op')}
          </KnobField>
          <KnobField>
            <KnobHelp label={t`Gap fill`} help={KNOB_HELP.gapFill} />
            <KnobSelect
              value={config.gap_fill}
              onChange={(e) =>
                onPatch({
                  gap_fill: e.target.value as ExperimentConfig['gap_fill'],
                })
              }>
              <option value='off'>
                <Trans>off (prod default)</Trans>
              </option>
              <option value='locf'>locf</option>
            </KnobSelect>
            {note('gap_fill')}
          </KnobField>
          {chartBuckets && (
            <>
              <KnobCheckRow>
                <Checkbox
                  checked={chartBuckets.showTrainingBuckets}
                  disabled={chartBuckets.unsupportedReason !== null}
                  onCheckedChange={(v) =>
                    chartBuckets.setShowTrainingBuckets(v === true)
                  }
                />
                <Trans>Show these buckets on the chart</Trans>
              </KnobCheckRow>
              {chartBuckets.unsupportedReason !== null ? (
                <KnobHint>{chartBuckets.unsupportedReason}</KnobHint>
              ) : (
                chartBuckets.showTrainingBuckets && (
                  <KnobHint>
                    <Trans>
                      Target and drivers get a stepped line on this grid,
                      fetched the same way training fetches it.
                    </Trans>
                  </KnobHint>
                )
              )}
            </>
          )}
        </>
      )}
      {config.sampling_mode === 'raw' && (
        <KnobHint>
          <Trans>
            Raw mode pulls every historian point and only aligns tags when
            timestamps match exactly. Prefer a shorter date range — dense tags
            can be huge.
          </Trans>
        </KnobHint>
      )}
    </>
  )
}
